import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Plus, Edit, Trash2, Percent, TrendingUp, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { payrollApi } from '../../services/api';
import { cn } from '../../lib/utils';

interface CommissionRule {
  id: string;
  name: string;
  description: string;
  rule_type: 'percentage' | 'tiered' | 'flat';
  percentage?: number;
  flat_amount?: number;
  min_sales?: number;
  max_sales?: number;
  applicable_to: string[];
  is_active: boolean;
  created_at: string;
}

const CommissionRules: React.FC = () => {
  const { toast } = useToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<CommissionRule | null>(null);
  const [rules, setRules] = useState<CommissionRule[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch commission rules from backend
  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const data = await payrollApi.commissionRules.getAll();
      setRules(data || []);
    } catch (error) {
      console.error('Failed to fetch commission rules:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to load commission rules',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rule_type: 'percentage' as 'percentage' | 'tiered' | 'flat',
    percentage: 0,
    flat_amount: 0,
    min_sales: 0,
    max_sales: 0,
    applicable_to: '',
    is_active: true,
  });

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Error', description: 'Please enter rule name', variant: 'destructive' });
      return;
    }

    try {
      const ruleData = {
        name: formData.name,
        description: formData.description,
        rule_type: formData.rule_type,
        percentage: formData.percentage || undefined,
        flat_amount: formData.flat_amount || undefined,
        min_sales: formData.min_sales || undefined,
        max_sales: formData.max_sales || undefined,
        applicable_to: formData.applicable_to.split(',').map(d => d.trim()).filter(Boolean),
        is_active: formData.is_active,
      };

      await payrollApi.commissionRules.create(ruleData);
      handleCloseDialog();
      toast({ title: 'Success', description: 'Commission rule created successfully' });
      fetchRules();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create commission rule',
        variant: 'destructive',
      });
    }
  };

  const handleUpdate = async () => {
    if (!editingRule || !formData.name.trim()) {
      return;
    }

    try {
      const ruleData = {
        name: formData.name,
        description: formData.description,
        rule_type: formData.rule_type,
        percentage: formData.percentage || undefined,
        flat_amount: formData.flat_amount || undefined,
        min_sales: formData.min_sales || undefined,
        max_sales: formData.max_sales || undefined,
        applicable_to: formData.applicable_to.split(',').map(d => d.trim()).filter(Boolean),
        is_active: formData.is_active,
      };

      await payrollApi.commissionRules.update(editingRule.id, ruleData);
      handleCloseDialog();
      toast({ title: 'Success', description: 'Commission rule updated successfully' });
      fetchRules();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update commission rule',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this commission rule?')) {
      return;
    }

    try {
      await payrollApi.commissionRules.delete(id);
      toast({ title: 'Success', description: 'Commission rule deleted successfully' });
      fetchRules();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete commission rule',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (rule: CommissionRule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description,
      rule_type: rule.rule_type,
      percentage: rule.percentage || 0,
      flat_amount: rule.flat_amount || 0,
      min_sales: rule.min_sales || 0,
      max_sales: rule.max_sales || 0,
      applicable_to: rule.applicable_to.join(', '),
      is_active: rule.is_active,
    });
  };

  const handleCloseDialog = () => {
    setIsCreateOpen(false);
    setEditingRule(null);
    setFormData({
      name: '',
      description: '',
      rule_type: 'percentage',
      percentage: 0,
      flat_amount: 0,
      min_sales: 0,
      max_sales: 0,
      applicable_to: '',
      is_active: true,
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const activeRules = rules.filter(r => r.is_active);

  const getRuleTypeBadge = (type: string) => {
    const typeStyles: Record<string, string> = {
      percentage: 'bg-blue-50 text-blue-700 border-blue-200/60 dark:bg-blue-900/20 dark:text-blue-400',
      flat: 'bg-indigo-50 text-indigo-700 border-indigo-200/60 dark:bg-indigo-900/20 dark:text-indigo-400',
      tiered: 'bg-purple-50 text-purple-700 border-purple-200/60 dark:bg-purple-900/20 dark:text-purple-400',
    };
    const classNames = typeStyles[type] || 'bg-slate-50 text-slate-700 border-slate-200';
    return (
      <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none capitalize shrink-0", classNames)} variant="outline">
        {type}
      </Badge>
    );
  };

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Configure commission structures and incentive programs</h2>
        </div>
        <div className="flex flex-wrap items-center gap-3 self-start md:self-auto">
          <Button 
            onClick={() => setIsCreateOpen(true)} 
          >
            <Plus className="h-4 w-4 mr-1.5" />
            <span>Add Rule</span>
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <p className="text-sm font-bold text-slate-400 tracking-wide">Total Rules</p>
                <p className="text-3xl font-bold text-slate-800 dark:text-white leading-none mt-1">{rules.length}</p>
              </div>
              <div className="p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-[#143A62] dark:text-blue-400 shadow-sm shrink-0">
                <Percent className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <p className="text-sm font-bold text-slate-400 tracking-wide">Active Rules</p>
                <p className="text-3xl font-bold text-slate-800 dark:text-white leading-none mt-1">{activeRules.length}</p>
              </div>
              <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500 shadow-sm shrink-0">
                <TrendingUp className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <p className="text-sm font-bold text-slate-400 tracking-wide">Avg Commission Rate</p>
                <p className="text-3xl font-bold text-slate-800 dark:text-white leading-none mt-1">
                  {activeRules.length > 0
                    ? `${(activeRules.reduce((sum, r) => sum + (r.percentage || 0), 0) / activeRules.length).toFixed(1)}%`
                    : '0%'}
                </p>
              </div>
              <div className="p-3 rounded-2xl bg-indigo-50 dark:bg-indigo-900/20 text-indigo-500 shadow-sm shrink-0">
                <Percent className="h-6 w-6 text-indigo-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commission Rules Table */}
      <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
        <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Commission Rules</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600 shrink-0" />
              <span className="ml-2 text-slate-400 font-bold text-xs">Loading commission rules...</span>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b-2 border-slate-50 dark:border-slate-800">
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Rule Name</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Type</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Rate/Amount</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Applicable To</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Status</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 text-right px-8">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-20 px-8 text-slate-400 font-semibold text-xs">
                      No commission rules configured
                    </TableCell>
                  </TableRow>
                ) : (
                rules.map((rule) => (
                  <TableRow key={rule.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-850/50">
                    <TableCell className="px-8 py-4">
                      <div>
                        <div className="font-bold text-xs text-slate-750 dark:text-slate-350">{rule.name}</div>
                        {rule.description && <div className="text-[10px] font-bold text-slate-400 mt-0.5">{rule.description}</div>}
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      {getRuleTypeBadge(rule.rule_type)}
                    </TableCell>
                    <TableCell className="text-xs font-black text-slate-850 dark:text-slate-150 py-4">
                      {rule.rule_type === 'percentage' && `${rule.percentage}%`}
                      {rule.rule_type === 'flat' && formatCurrency(rule.flat_amount || 0)}
                      {rule.rule_type === 'tiered' && (
                        <div className="text-xs font-black">
                          <div>{rule.percentage}%</div>
                          {rule.min_sales && (
                            <div className="text-[10px] font-semibold text-slate-400 mt-0.5">
                              Min: {formatCurrency(rule.min_sales)}
                            </div>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="flex flex-wrap gap-1">
                        {rule.applicable_to.slice(0, 2).map((dept, idx) => (
                          <Badge key={idx} variant="secondary" className="px-2 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border border-emerald-250/50 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400 shadow-none capitalize">
                            {dept}
                          </Badge>
                        ))}
                        {rule.applicable_to.length > 2 && (
                          <Badge variant="secondary" className="px-2 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border border-slate-200 bg-slate-50 text-slate-600 dark:bg-slate-850 dark:text-slate-350 shadow-none">
                            +{rule.applicable_to.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-4">
                      <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none shrink-0", rule.is_active ? "bg-cyan-50 text-cyan-700 border-cyan-200/50 dark:bg-cyan-900/20 dark:text-cyan-400" : "bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400")} variant="outline">
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-8 py-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(rule)}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(rule.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateOpen || !!editingRule} onOpenChange={handleCloseDialog}>
        <DialogContent className="w-[min(96vw,550px)] max-w-[550px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 flex items-center gap-2">
              <Percent className="h-5 w-5 text-blue-500" />
              <span>{editingRule ? 'Edit' : 'Create'} Commission Rule</span>
            </DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              Configure commission rates and conditions for your sales team
            </DialogDescription>
          </DialogHeader>
          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs font-bold text-slate-400 tracking-wider">Rule Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Q4 Sales Bonus"
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="description" className="text-xs font-bold text-slate-400 tracking-wider">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this commission rule"
                rows={2}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-sm"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rule_type" className="text-xs font-bold text-slate-400 tracking-wider">Rule Type *</Label>
              <select
                id="rule_type"
                className="w-full px-3 h-11 border border-slate-200 dark:border-slate-800 rounded-xl focus:ring-2 focus:ring-blue-500/20 font-bold text-sm bg-white dark:bg-slate-900"
                value={formData.rule_type}
                onChange={(e) => setFormData({ ...formData, rule_type: e.target.value as any })}
              >
                <option value="percentage">Percentage of Sales</option>
                <option value="tiered">Tiered (Based on Sales Volume)</option>
                <option value="flat">Flat Amount</option>
              </select>
            </div>

            {formData.rule_type === 'percentage' && (
              <div className="space-y-1.5">
                <Label htmlFor="percentage" className="text-xs font-bold text-slate-400 tracking-wider">Commission Percentage</Label>
                <Input
                  id="percentage"
                  type="number"
                  step="0.1"
                  value={formData.percentage}
                  onChange={(e) => setFormData({ ...formData, percentage: parseFloat(e.target.value) })}
                  placeholder="5"
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                />
              </div>
            )}

            {formData.rule_type === 'flat' && (
              <div className="space-y-1.5">
                <Label htmlFor="flat_amount" className="text-xs font-bold text-slate-400 tracking-wider">Flat Amount</Label>
                <Input
                  id="flat_amount"
                  type="number"
                  value={formData.flat_amount}
                  onChange={(e) => setFormData({ ...formData, flat_amount: parseFloat(e.target.value) })}
                  placeholder="1000"
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                />
              </div>
            )}

            {formData.rule_type === 'tiered' && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="tiered_percentage" className="text-xs font-bold text-slate-400 tracking-wider">Commission Percentage</Label>
                  <Input
                    id="tiered_percentage"
                    type="number"
                    step="0.1"
                    value={formData.percentage}
                    onChange={(e) => setFormData({ ...formData, percentage: parseFloat(e.target.value) })}
                    placeholder="10"
                    className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="min_sales" className="text-xs font-bold text-slate-400 tracking-wider">Minimum Sales</Label>
                    <Input
                      id="min_sales"
                      type="number"
                      value={formData.min_sales}
                      onChange={(e) => setFormData({ ...formData, min_sales: parseFloat(e.target.value) })}
                      placeholder="0"
                      className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="max_sales" className="text-xs font-bold text-slate-400 tracking-wider">Maximum Sales (Optional)</Label>
                    <Input
                      id="max_sales"
                      type="number"
                      value={formData.max_sales}
                      onChange={(e) => setFormData({ ...formData, max_sales: parseFloat(e.target.value) })}
                      placeholder="Leave empty for no limit"
                      className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="applicable_to" className="text-xs font-bold text-slate-400 tracking-wider">Applicable To (Departments)</Label>
              <Input
                id="applicable_to"
                value={formData.applicable_to}
                onChange={(e) => setFormData({ ...formData, applicable_to: e.target.value })}
                placeholder="Sales, Business Development (comma separated)"
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
              />
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800">
              <Label htmlFor="is_active" className="text-xs font-bold text-slate-400 tracking-wider">Active</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>
          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button onClick={editingRule ? handleUpdate : handleCreate}>
              {editingRule ? 'Update' : 'Create'} Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommissionRules;